/* eslint-disable jsx-a11y/control-has-associated-label */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ICheckedTableItems,
  IFilterTripsOverview,
  ITripsOverViewRecord,
  ITripsOverviewAdvancedFilterForRequest,
  ITripsOverviewInfo,
  ITripsOverviewTableData,
} from '../interfaces';
import { selectedVehiclesAtom } from '../../../store/recoil/vehicles';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  filterForRequestAtom,
  hasMoreAtom,
  isActiveAdvanceFilterAtom,
  offsetAtom,
  rideBookDataAtom,
  rideItemAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import {
  convertTimeToSeconds,
  editComment,
  handleFilterKey,
  removeComment,
} from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

interface ICommentsProps {
  rideId: string;
  customerId: number;
  comment: string | null;
  isParking?: boolean;
  isModal?: boolean;
  isEditable: boolean;
  isRemovable: boolean;
}

function Comment(props: ICommentsProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const setRideItem = useSetRecoilState(rideItemAtom(String(props.rideId)));
  const [errorCommentInput, setErrorCommentInput] = React.useState<string>('');
  const [editingComment, setEditingComment] = React.useState<{ [key: string]: string } | null>(
    null
  );
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const filterForRequest = useRecoilValue<IFilterTripsOverview>(filterForRequestAtom);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const isActiveAdvanceFilter = useRecoilValue(isActiveAdvanceFilterAtom);
  const advancedFilterValues = useRecoilValue(advancedFiltersAtom);
  const { trackEvent } = useAnalytics();

  const resetState = () => {
    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setRideItemIds([]);
    setHasMore(true);
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });
  };

  const deleteComment = async () => {
    trackEvent(['ride book', 'click', 'Ride book delete comment']);
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );
    const filter = {
      vehicle: selectedVehicles,
      ...advancedFilters,
      ...filterForRequest,
    };
    const res = await removeComment(props.customerId, props.rideId, filter, props.isParking);

    if (res) {
      if (isActiveAdvanceFilter.comment) {
        resetState();
      } else {
        setRideItem((oldState: ITripsOverViewRecord | null) => {
          if (oldState) {
            const newState = { ...oldState };
            const infoData: ITripsOverviewInfo = { ...oldState.info };
            infoData[props.isParking ? 'parkingComment' : 'rideComment'] = null;
            if (props.isParking && oldState.parking) {
              const parkingData = { ...oldState.parking };
              parkingData.summary = {
                ...parkingData.summary,
                comment: Number(parkingData.summary?.comment) - 1,
              };
              newState.parking = { ...parkingData };
            } else {
              infoData.summary = {
                ...infoData.summary,
                comment: Number(infoData.summary.comment) - 1,
              };
            }
            newState.info = { ...infoData };
            return newState;
          }
          return null;
        });
      }
    }
  };

  const updateComment = async () => {
    const comment = editingComment ? editingComment[props.isParking ? 'parking' : 'ride'] : '';
    trackEvent(['ride book', 'click', 'Ride book edit comment']);
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );
    const filter = {
      vehicle: selectedVehicles,
      ...advancedFilters,
      ...filterForRequest,
    };

    const res = await editComment(comment, props.customerId, props.rideId, filter, props.isParking);
    if (res) {
      setEditingComment(null);
      if (isActiveAdvanceFilter.comment) {
        resetState();
      } else {
        setRideItem((oldState: ITripsOverViewRecord | null) => {
          if (oldState) {
            const newState = { ...oldState };
            const infoData = { ...oldState.info };
            infoData[props.isParking ? 'parkingComment' : 'rideComment'] = comment;
            newState.info = { ...infoData };
            return newState;
          }
          return null;
        });
      }
    }
  };

  const removeElement = () => {
    if (props.isRemovable) {
      return (
        <a
          href="#"
          title={f({ id: 'tripsOverview.modal.removeComment' })}
          className="text-gray-lighten cmd-icons cmd-icon-bin mx-1"
          onClick={deleteComment}
          data-cy="tripsOverview-modal-removePurpose"
        />
      );
    }
    return false;
  };

  const handleComment = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const isSetText =
      (props.isParking && editingComment && editingComment.parking) ||
      (!props.isParking && editingComment && editingComment.ride);
    const text = isSetText
      ? editingComment[props.isParking ? 'parking' : 'ride']
      : props.comment || '';
    setEditingComment({ [props.isParking ? 'parking' : 'ride']: text });
  };

  const cancelComment = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setEditingComment(null);
    setErrorCommentInput('');
    trackEvent(['ride book', 'click', 'Ride book cancel comment']);
  };

  const handleCommentOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    const newText = event.target.value;
    setEditingComment({ [props.isParking ? 'parking' : 'ride']: newText });
    trackEvent(['ride book', 'click', 'Ride book edit comment']);
    if (newText.length < 1) {
      setErrorCommentInput('form.required');
    } else if (newText.length > 200) {
      setErrorCommentInput('form.numberMax200');
    } else {
      setErrorCommentInput('');
    }
  };

  const editElement = () => {
    if (props.isEditable) {
      return (
        <a
          href="#"
          className="text-gray-lighten cmd-icons cmd-icon-edit js-edit-comment"
          data-cy="edit-ride-book-comment" // Used by Cypress tests
          onClick={handleComment}
          title={f({ id: 'tripsOverview.modal.editComment' })}
        />
      );
    }
    return false;
  };

  const getEditingComment = () => {
    if (editingComment) {
      return editingComment[props.isParking ? 'parking' : 'ride'] || '';
    }
    return '';
  };

  const editingElement = () => {
    return (
      <>
        <div className={`b-edit-input-field active ${errorCommentInput ? 'err-border' : ''}`}>
          <textarea onChange={handleCommentOnChange} style={{ borderColor: '#3669BB' }}>
            {getEditingComment()}
          </textarea>

          <div className="b-edit-input-field-actions">
            <a
              className="item cmd-icon-check"
              onClick={(e) => {
                e.preventDefault();
                if (!errorCommentInput) {
                  updateComment();
                }
              }}
              href="#"
              title={f({ id: 'rides.modal.comment.save.tooltip' })}
            />
            <a
              className="item cmd-icon-undo"
              onClick={cancelComment}
              href="#"
              title={f({ id: 'form.cancel.tooltip' })}
            />
          </div>
        </div>
        {errorCommentInput && <div className="err-color">{f({ id: errorCommentInput })}</div>}
      </>
    );
  };

  return (
    <div
      className={
        props.isModal
          ? 'row no-gutters text-blue-dark flex-nowrap pl-2 py-1'
          : 'row no-gutters text-blue-dark flex-nowrap py-1 pr-0 w-100'
      }
      style={{ overflow: 'hidden' }}
    >
      <div className="col-auto text-blue-dark pt-1" key={props.rideId}>
        <i
          className={`cmd-icons cmd-icon-commentary
            fs-4`}
        />
      </div>
      <div className="col pl-2">
        {editingComment ? (
          editingElement()
        ) : (
          <div className="b-edit-input-field m-0">
            {props.comment}
            <div className="b-edit-input-field-actions" key="tagBefore">
              {editElement()}
              {removeElement()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Comment;
